import { Spinner, SpinnerSize } from '@blueprintjs/core';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';
import { usePortalUser } from 'vet-bones/utils/portal';

import {
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
  SUPPORTED_DATA_SOURCE_FAMILIES,
} from 'src/constants/dataSourceTypeDescription';
import { SAMPLE_MUSIC_DATA_SOURCE } from 'src/constants/sampleDataSource';
import { DataSourceTypeSelectionPanelIcon } from 'src/containers/DataSourceCreation/DataSourceTypeSelectionPanelIcon';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceTypeListSelector } from 'src/selectors/dataSourceAvailableTypeList';
import { dataSourceFamiliesByTypeSelector } from 'src/selectors/dataSourceFamiliesByType';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { isUrlStardogFreeCloudInstance } from 'src/utils/web/isUrlStardogCloudInstance';

type DataSourceTypeCardsProps = {
  isLoading: boolean;
  isSampleLoading: boolean;
  onSelectDataSourceType: (dataSourceType: string) => void;
};

export const DataSourceTypeCards: React.VFC<DataSourceTypeCardsProps> = ({
  onSelectDataSourceType,
  isLoading,
  isSampleLoading,
}) => {
  const portalUser = usePortalUser();

  const connection = useReduxSelector(connectionSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const dataSourceTypeList = useReduxSelector(dataSourceTypeListSelector);
  const isDataSourceDescriptionEndpointSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceDescriptionEndpointSupported',
      connection
    )
  );
  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );
  const dataSourceFamiliesByType = useReduxSelector(
    dataSourceFamiliesByTypeSelector(connection)
  );

  const typeList = isDataSourceDescriptionEndpointSupported
    ? Array.from(dataSourceTypeList).filter(
        (dataSourceType) =>
          SUPPORTED_DATA_SOURCE_FAMILIES[
            dataSourceFamiliesByType[dataSourceType]
          ]
      )
    : (Object.keys(dataSourcesByType) as DataSourceType[]).filter(
        (dataSourceType) =>
          SUPPORTED_DATA_SOURCE_FAMILIES[
            dataSourceFamiliesByType[dataSourceType]
          ]
      );
  typeList.sort(
    alphaSort((dataSourceType) => dataSourceLabelsByType[dataSourceType])
  );
  if (
    (isUrlStardogFreeCloudInstance(connection?.endpoint || '') ||
      portalUser.data?.is_staff) &&
    typeList.includes(SAMPLE_MUSIC_DATA_SOURCE.type)
  ) {
    // Add the sample data source only when we support the underlying data source
    // type (MYSQL); also add it after the sort so we show the button end of the list.
    typeList.push(SAMPLE_MUSIC_DATA_SOURCE_TYPE);
  }

  const iconList: React.ReactElement[] = typeList.map((dataSourceType) => (
    <DataSourceTypeSelectionPanelIcon
      key={dataSourceType}
      dataSourceType={dataSourceType}
      isSampleLoading={isSampleLoading}
      onClick={() => {
        onSelectDataSourceType(dataSourceType);
      }}
    />
  ));

  if (isLoading) {
    return (
      <Spinner
        className="sd-data-source-panel-loading"
        size={SpinnerSize.LARGE}
      />
    );
  }

  return iconList.length === 0 ? (
    <div className="sd-data-source-panel-empty">
      {
        copy.components.projectResourceDialog.dataSourceTypeSelectionPanel
          .noDataSources
      }
    </div>
  ) : (
    <div className="sd-data-source-panel-cards">{iconList}</div>
  );
};
